import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useFetchWithBody, usePost } from '../../api/reactQueryCustomHooks';
import {
    Box, Button, Grid, Paper, Autocomplete,
    TableHead, TableRow, TextField, Typography, FormControl, InputLabel,
    Select, MenuItem, IconButton, InputAdornment, Popover, useMediaQuery, Chip, Alert, AlertTitle, ListSubheader,
    CircularProgress
} from '@mui/material';

import { format, toDate } from 'date-fns';
import { apiRoute, ENUM_MOD_NAME, ENUM_MOD_PARAMETER, getModMandatory } from '../../constants/enum';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faDownload, faDownLong, faFilter, faInfo, faRefresh, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ErrorCard, LoadingCard, MainContainer, MUIDataTable } from '../../components';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from "dayjs"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { head } from 'lodash';
import { toast } from 'react-toastify';

export const ALL_COLUMNS = {

    Chq_Date: { label: "Date", group: "date", type: "date" },
    YearMonth: { label: "Month", group: "month", type: "string" },
    AC_DESC: { label: "Account", group: "account", type: "string" },
    PAc_Desc: { label: "Party", group: "party", type: "string" },
    BankName: { label: "Bank", group: "none", type: "string" },
    Chq_No: { label: "Cheque No", group: "none", type: "string" },
    Chq_Amt: { label: "Amount", group: "none", type: "number" },
    Remark: { label: "Remark", group: "none", type: "string" },


};

export const getColumns = (groupBy, activeTab, useMktAsMfg = true) => {
    return Object.entries(ALL_COLUMNS)
        .filter(([key, col]) => {
            // Handle SL_Code
            const bothgroup = ['Chq_Amt']
            if (bothgroup.includes(key))
                return groupBy === "none" || groupBy === "party" || groupBy === "account" || groupBy === "date" || groupBy === "month";
            if (key == "Chq_Date")
                return groupBy === "none" || groupBy === "date";
            if (key == "AC_DESC")
                return groupBy === "none" || groupBy === "account";
            if (key == "PAc_Desc")
                return groupBy === "none" || groupBy === "party";


            // Handle grouped columns
            if (col.group && col.group !== groupBy) return false;


            return true;
        })
        .map(([key, col]) => ({ key, ...col }));
};
;


const ReceivePaymentReport = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { compCode, compName, startDate, endDate } = useSelector((state) => state.company);
    const tableRef = useRef(null);
    const [groupBy, setGroupBy] = useState('none');
    const [isUseMfgNoAsMktno, setIsUseMfgNoAsMktno] = useState(true)
    const [columnFilters, setColumnFilters] = useState({
        Chq_Date: '',
        YearMonth: '',
        AC_DESC: '',
        PAc_Desc: '',
        BankName: '',
        Chq_No: '',
        Chq_Amt: '',
        Remark: '',
    });

    const [filters, setFilters] = useState({
        compCode: compCode,
        dy: '1',
        fromDate: startDate,//new Date(new Date().setDate(new Date().getDate() - 30)),
        toDate: endDate,// new Date(),
        Vou_Type: 'R',
        AC_ID: ''
    });
    const exportColumnKeys = [
        "Chq_Date",
        "AC_DESC",
        "PAc_Desc",
        "BankName",
        "Chq_No",
        "Chq_Amt",
        "Remark", "YearMonth"
    ];

    const { isError: isErrorModSet, isLoading, data: modSetting = [] } = useFetchWithBody({
        url: apiRoute.modSetting.getModSetting,
        queryKey: "fetchModSettingInwardItem",
        inputData: {
            mod_name: ENUM_MOD_NAME.GeneralSettings
        }
    })
    useEffect(() => {
        if (modSetting && modSetting.length > 0) {
            const mktSetting = getModMandatory(ENUM_MOD_PARAMETER.GeneralSetting.UseMfgNoAsMktNo, modSetting);
            setIsUseMfgNoAsMktno(mktSetting)
        }
    }, [modSetting])

    const { data: accounts = [] } = useFetchWithBody({
        url: apiRoute.transactionEntry.getParties,
        queryKey: ['paymentReportAccounts'],
        inputData: { compCode }
    });



    const {
        data: reportData = [],
        isPending,
        isError,
        error,
        mutate: fetchReportData
    } = usePost({
        url: apiRoute.transactionEntry.getReportData,
        queryKey: ['paymentReport'],
    });



    // // Apply column filters to the data
    const filteredData = useMemo(() => {
        if (!reportData.length) return [];
        return reportData.filter(row => {
            return Object.entries(columnFilters).every(([key, value]) => {
                if (!value) return true;
                const rowValue = row[key] || '';
                return String(rowValue).toLowerCase().includes(String(value).toLowerCase());
            });
        });
    }, [reportData, columnFilters]);




    const filteredAndGroupedData = useMemo(() => {
        if (!reportData?.length) return [];

        // ---------- FILTER ----------
        // const filteredData = reportData.filter(row =>
        //     Object.entries(columnFilters).every(([key, value]) => {
        //         if (!value) return true;
        //         return String(row[key] ?? '')
        //             .toLowerCase()
        //             .includes(String(value).toLowerCase());
        //     })
        // );

        if (groupBy === 'none') return filteredData;

        /* ================= GROUPING ================= */

        const groupedData = {};


        const getGroupKey = (row) => {
            switch (groupBy) {
                case 'account':
                    return row.AC_DESC || 'Account';

                case 'party':
                    return row.PAc_Desc || 'Party';

                case 'date':
                    return format(new Date(row.Chq_Date), 'yyyy-MM-dd');

                case 'month':
                    return row.YearMonth || 'Unknown Month';

                default:
                    return 'all';
            }
        };

        const initGroup = (row, key) => ({
            groupKey: key,
            Chq_Date: groupBy === 'date'
                ? format(new Date(row.Chq_Date), 'dd-MMM-yyyy')
                : groupBy === 'month'
                    ? format(new Date(row.Chq_Date), 'MMMM yyyy')
                    : key,
            AC_DESC: groupBy === 'account' ? row.AC_DESC : key,
            PAc_Desc: groupBy === 'party' ? row.PAc_Desc : key,
            BankName: groupBy === 'bank' ? row.BankName : key,
            YearMonth: groupBy === 'month' ? row.YearMonth : key,
            Chq_No: 0,
            Chq_Amt: 0,
            Remark: "",

            // weighted helpers
            //  _rateWeightedSum: 0,
            //  _weightSum: 0,

            _children: []
        });

        filteredData.forEach(row => {
            const key = getGroupKey(row);

            if (!groupedData[key]) {
                groupedData[key] = initGroup(row, key);
            }

            const group = groupedData[key];

            const add = (field) => {
                group[field] += Number(row[field]) || 0;
            };

            add('Chq_Amt');


            // Example weighted logic (if needed)
            // if (row.Rate && row.Weight) {
            //     group._rateWeightedSum += Number(row.Rate) * Number(row.Weight);
            //     group._weightSum += Number(row.Weight);
            // }

            group._children.push(row);
        });

        /* ================= FINALIZE ================= */

        return Object.values(groupedData)
            .map(({ _rateWeightedSum, _weightSum, ...rest }) => ({
                ...rest,
                Rate: _weightSum ? _rateWeightedSum / _weightSum : 0
            }))
            .sort((a, b) =>
                String(a.groupKey).localeCompare(String(b.groupKey))
            );

    }, [
        filteredData,
        columnFilters,
        filters,
        groupBy
    ]);





    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };




    const handleSubmit = (e) => {
        e.preventDefault();
        if ((filters.fromDate && filters.fromDate == 'Invalid Date')
            || (filters.toDate && filters.toDate == 'Invalid Date')) {
            toast.error("Invalid From Date / To Date", {
                "position": "top-center"
            })
        }
        else {
            const params = {
                ...filters,
                fromDate: format(filters.fromDate, 'yyyy-MM-dd'),
                toDate: format(filters.toDate, 'yyyy-MM-dd'),
                AC_ID: filters.AC_ID || ''
            };
            fetchReportData({ data: params });
        }

        // refetch();
    };

    const handleReset = () => {
        setFilters({
            compCode: compCode.join(","),
            dy: '1',
            fromDate: startDate,// new Date(new Date().setDate(new Date().getDate() - 30)),
            toDate: endDate,// new Date(),
            Vou_Type: 'R'
        });
        setGroupBy("none")
        const params = {
            compCode: compCode.join(","),
            dy: '1',
            Vou_Type: 'R',
            fromDate: startDate,
            toDate: endDate,
            AC_ID: filters.AC_ID || ''
        };
        fetchReportData({ data: params });
    };



    // const calculateTotals = () => {
    //     const totals = {
    //         invAmt: 0,
    //         TdsAmt: 0,
    //         PayAmt: 0,
    //         retAmt: 0,
    //         BalanceAmt: 0
    //     };

    //     filteredData.forEach(row => {
    //         totals.invAmt += parseFloat(row.invAmt) || 0;
    //         totals.TdsAmt += parseFloat(row.TdsAmt) || 0;
    //         totals.PayAmt += parseFloat(row.PayAmt) || 0;
    //         totals.retAmt += parseFloat(row.retAmt) || 0;
    //         totals.BalanceAmt += parseFloat(row.BalanceAmt) || 0;
    //     });

    //     return totals;
    // };


    // const totals = calculateTotals();

    const getReportTitle = () => {
        return filters.Vou_Type === 'R' ? 'Receive Register' : 'Payment Register';
    };


    const headCssStyle = { fontSize: "0.7rem", lineHeight: 1.3, textTransform: "uppercase", backgroundColor: '#f0f0f0', fontWeight: 600, color: '#3c3c3c' }
    const columns = useMemo(() => getColumns(groupBy, "all", isUseMfgNoAsMktno), [isUseMfgNoAsMktno, groupBy]);
    if (isError) {
        return <ErrorCard />
    }
    if (isLoading) {
        return <LoadingCard />
    }
    return (
        <MainContainer
            pageName={`Receive / Payment List`}
            breadcrumbs={["Reports", "Receive / Payment List"]}

        >
            <Box sx={{
                p: isMobile ? 1 : 3,
                backgroundColor: 'background.default',
                minHeight: '100vh',

            }}>
                {/* Header Section */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 2,
                    mb: 1,
                    p: 1,
                    backgroundColor: 'background.paper',
                    borderRadius: 3,
                    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)'
                }}>
                    <Typography variant="h4" sx={{
                        fontSize: isMobile ? '1rem' : '1.3rem',
                        fontWeight: 400,
                        color: 'primary.main',
                        letterSpacing: -0.5
                    }}>
                        {getReportTitle()}
                    </Typography>

                    {reportData.length > 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            alignSelf: { xs: 'stretch', md: 'center' }
                        }}>

                        </Box>
                    )}
                </Box>

                {/* Filters Section */}
                <Paper sx={{
                    p: { xs: 2, sm: 2 },
                    mb: 1,
                    borderRadius: 3,
                    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
                    backgroundColor: 'background.paper'
                }}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2} alignItems="center">
                            {/* Hidden fields */}
                            <Grid item xs={12} sm={6} md={3} sx={{ display: 'none' }}>
                                <TextField fullWidth label="Company Code" name="compCode" value={filters.compCode} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} sx={{ display: 'none' }}>
                                <TextField fullWidth label="DY" name="dy" value={filters.dy} />
                            </Grid>

                            {/* Main form controls */}
                            <Grid item xs={12} sm={6} md={3} lg={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Voucher Type</InputLabel>
                                    <Select
                                        name="Vou_Type"
                                        value={filters.Vou_Type}
                                        onChange={handleFilterChange}
                                        label="Voucher Type"
                                        sx={{
                                            '& .MuiSelect-select': { py: 1 }
                                        }}
                                    >
                                        <MenuItem value="R">Receive Register</MenuItem>
                                        <MenuItem value="G">Payment Register</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3} lg={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Account</InputLabel>
                                    <Select
                                        name="AC_ID"
                                        value={filters.AC_ID}
                                        onChange={handleFilterChange}
                                        label="Account"
                                        sx={{
                                            '& .MuiSelect-select': { py: 1 }
                                        }}
                                    >
                                        <MenuItem value="">All Accounts</MenuItem>
                                        {accounts.map((account) => (
                                            <MenuItem key={account.AC_ID} value={account.AC_ID}>
                                                {account.AC_DESC}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3} lg={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label={"From Date"}
                                        views={["day", "month", "year"]}
                                        format="DD/MM/YYYY"
                                        value={
                                            filters.fromDate
                                                ? dayjs(filters.fromDate)
                                                : filters.fromDate
                                        }
                                        onChange={(newValue) => {
                                            setFilters((prev) => {
                                                return {
                                                    ...prev,
                                                    fromDate: newValue != '' && newValue != 'Invalid Date' ?
                                                        dayjs(newValue).format("YYYY-MM-DD") : null,
                                                };
                                            });
                                        }}
                                        //isRequired={item.required}
                                        // className="w-full "
                                        slotProps={{
                                            textField: { size: "small", },
                                        }}
                                    />
                                </LocalizationProvider>

                            </Grid>

                            <Grid item xs={12} sm={6} md={3} lg={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label={"To Date"}
                                        views={["day", "month", "year"]}
                                        format="DD/MM/YYYY"
                                        value={
                                            filters.toDate
                                                ? dayjs(filters.toDate)
                                                : filters.toDate
                                        }
                                        onChange={(newValue) => {
                                            setFilters((prev) => {
                                                return {
                                                    ...prev,
                                                    toDate: newValue != '' && newValue != 'Invalid Date' ?
                                                        dayjs(newValue).format("YYYY-MM-DD") : null,
                                                };
                                            });
                                        }}
                                        //isRequired={item.required}
                                        //className="w-full "
                                        slotProps={{
                                            textField: { size: "small", },
                                        }}
                                    />
                                </LocalizationProvider>
                                
                            </Grid>

                            <Grid item xs={12} sm={6} md={3} lg={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Group By</InputLabel>
                                    <Select
                                        label="Group By"
                                        value={groupBy}
                                        onChange={(e) => setGroupBy(e.target.value)}
                                        sx={{
                                            '& .MuiSelect-select': { py: 1 }
                                        }}
                                    >
                                        <MenuItem value="none">Report Detail</MenuItem>
                                        <MenuItem value="account">Account</MenuItem>
                                        <MenuItem value="party">Party</MenuItem>
                                        <MenuItem value="date">Date</MenuItem>
                                        <MenuItem value="month">Month</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Action buttons - responsive behavior */}
                            <Grid item xs={12} sm={6} md={3} lg={2}>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 1,
                                    flexDirection: { xs: 'row', sm: 'row-reverse' },
                                    justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                                }}>


                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleReset}
                                        startIcon={<FontAwesomeIcon className="text-xs" icon={faRefresh} />}
                                        sx={{

                                            minWidth: 80,
                                            //py: 1,
                                            borderRadius: 2,
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            borderWidth: 1.5,
                                            '&:hover': { borderWidth: 1.5 },
                                            flexGrow: { xs: 1, sm: 0 }
                                        }}
                                    >
                                        Reset
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={isPending}
                                        startIcon={isPending ? <CircularProgress size={15} color="inherit" /> :
                                            <FontAwesomeIcon className='text-xs' icon={faSearch}></FontAwesomeIcon>}
                                        sx={{
                                            minWidth: 85,
                                            // py: 0,
                                            borderRadius: 2,
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            // fontSize:"0.7rem",
                                            flexGrow: { xs: 1, sm: 0 }
                                        }}
                                    >
                                        {isLoading ? 'Loading' : 'Search'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </Paper>



                {/* Error Message */}


                {/* Data Table Section */}
                <Paper sx={{
                    p: isMobile ? 1 : 1,
                    borderRadius: 3,
                    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
                    backgroundColor: 'background.paper'
                }} ref={tableRef}>
                    {/* Table Header */}


                    {/* Data Table */}
                    {reportData.length > 0 ? (
                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                            <MUIDataTable
                                data={filteredAndGroupedData}
                                dataColumns={columns}
                                reportTitle={`${filters.Vou_Type === 'R' ? 'Receive' : 'Payment'} Register Report`}
                                orderGroup={groupBy}
                                currency={"all"}
                                filterDate={filters}
                                tradeLabel={`Type: ${filters.Vou_Type === 'R' ? 'Receive' : 'Payment'} `}
                                companyName={compName.join(",")}
                                hideIfZeroKeys={[]}
                                exportColumnKeys={exportColumnKeys}
                                enableHeaderFilter={true}

                            />
                        </Box>
                    ) : (
                        !isPending && (
                            <Box sx={{
                                p: 4,
                                textAlign: 'center',
                                border: `1px dashed ${theme.palette.divider}`,
                                borderRadius: 2,
                                backgroundColor: 'background.default'
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 2
                                }}>
                                    <FontAwesomeIcon icon={faInfo} />
                                    {/* <DescriptionOutlined sx={{
                  fontSize: 48,
                  color: 'text.disabled'
                }} /> */}
                                    <Typography variant="h6" color="text.secondary">
                                        No  data available
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        Apply filters above to generate a report
                                    </Typography>
                                </Box>
                            </Box>
                        )
                    )}
                </Paper>


            </Box>
        </MainContainer>
    );
};

export default ReceivePaymentReport;
